import { ApplicationConfig, provideZoneChangeDetection, APP_INITIALIZER, inject } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideIonicAngular } from '@ionic/angular/standalone';
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { provideAuth, getAuth } from '@angular/fire/auth';
import { browserLocalPersistence, setPersistence } from '@angular/fire/auth';
import { provideFirestore } from '@angular/fire/firestore';
import { getApp } from 'firebase/app';
import { initializeFirestore, persistentLocalCache, persistentSingleTabManager } from 'firebase/firestore';
import { provideStorage, getStorage } from '@angular/fire/storage';
import { provideMessaging, getMessaging } from '@angular/fire/messaging';
import { routes } from './app.routes';
import { environment } from '../environments/environment';
import { provideHttpClient, HTTP_INTERCEPTORS, withInterceptorsFromDi } from '@angular/common/http';
import { provideI18n } from '@shared';
import { ErrorHandler } from '@angular/core';
import { GlobalErrorHandler } from '@shared';
import {
  ApiConfigService,
  AuthInterceptor,
  OfflineInterceptor,
  OfflineCacheService,
  PRODUCTS_SKIP_VENDOR_MARKUP,
} from '@shared';
import { NoOfflineQueueInterceptor } from './interceptors/no-offline-queue.interceptor';

// Initialize API configuration
function initializeApiConfig() {
  return () => {
    const apiConfig = inject(ApiConfigService);

    // Replace localhost with network IP for mobile devices (Android/iOS).
    let apiUrl = environment.apiUrl;
    let wsUrl = environment.wsUrl;

    if (typeof window !== 'undefined') {
      const localIp = (environment as any).localNetworkIp || '10.0.0.75';
      try {
        const Capacitor = (window as any).Capacitor;
        if (Capacitor && Capacitor.isNativePlatform && Capacitor.isNativePlatform() && Capacitor.getPlatform?.() === 'android') {
          const isEmulator = window.location.hostname === '10.0.2.2' || window.location.hostname === 'localhost';
          if (isEmulator) {
            apiUrl = environment.apiUrl.replace('localhost', '10.0.2.2');
            wsUrl = environment.wsUrl.replace('localhost', '10.0.2.2');
          } else {
            apiUrl = environment.apiUrl.replace('localhost', localIp);
            wsUrl = environment.wsUrl.replace('localhost', localIp);
          }
        } else if (Capacitor && Capacitor.isNativePlatform && Capacitor.isNativePlatform()) {
          // Native platform (non-android): use LAN IP
          apiUrl = environment.apiUrl.replace('localhost', localIp);
          wsUrl = environment.wsUrl.replace('localhost', localIp);
        }
      } catch {
        // Ignore
      }
    }

    apiConfig.setApiUrl(apiUrl);
    apiConfig.setWsUrl(wsUrl);
    return Promise.resolve();
  };
}

console.log('[Vendor App Config] Loading app config...');
console.log('[Vendor App Config] Routes:', routes);

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideIonicAngular({
      mode: 'ios',
      rippleEffect: true,
      hardwareBackButton: true,
    }),
    provideHttpClient(withInterceptorsFromDi()),
    { provide: HTTP_INTERCEPTORS, useClass: NoOfflineQueueInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: OfflineInterceptor, multi: true },
    provideI18n(),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: APP_INITIALIZER, useFactory: initializeApiConfig, multi: true },
    { provide: PRODUCTS_SKIP_VENDOR_MARKUP, useValue: true },
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        return () => {
          const cacheService = inject(OfflineCacheService);
          cacheService.initialize();
          return Promise.resolve();
        };
      },
      multi: true,
    },
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() => {
      const auth = getAuth();
      // Set persistence to LOCAL for web - persists across page refreshes
      // For mobile (Capacitor), Firebase Auth automatically uses native storage
      if (typeof window !== 'undefined' && !(window as { Capacitor?: unknown }).Capacitor) {
        setPersistence(auth, browserLocalPersistence).catch(() => {
          // Error will be handled by global error handler
        });
      }
      return auth;
    }),
    provideFirestore(() =>
      initializeFirestore(getApp(), {
        localCache: persistentLocalCache({ tabManager: persistentSingleTabManager(undefined) }),
        experimentalAutoDetectLongPolling: true,
      }),
    ),
    provideStorage(() => getStorage()),
    provideMessaging(() => getMessaging()),
  ],
};
